import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getOrder, updateOrder } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Tag, Package } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const statusConfig = {
  pending: { label: '受付中', className: 'bg-amber-100 text-amber-800' },
  confirmed: { label: '確定', className: 'bg-blue-100 text-blue-800' },
  shipped: { label: '発送済', className: 'bg-purple-100 text-purple-800' },
  completed: { label: '完了', className: 'bg-emerald-100 text-emerald-800' },
  cancelled: { label: 'キャンセル', className: 'bg-red-100 text-red-800' },
};

export const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchOrder(); }, [id]);

  const fetchOrder = async () => {
    try {
      const res = await getOrder(id);
      setOrder(res.data);
    } catch (error) {
      toast.error('注文の取得に失敗しました');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await updateOrder(id, { status: newStatus });
      toast.success('ステータスを更新しました');
      fetchOrder();
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  const handlePrint = () => { window.print(); };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  if (!order) return null;

  const status = statusConfig[order.status] || statusConfig.pending;

  return (
    <div className="space-y-6 fade-in" data-testid="order-detail-page">
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/orders')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{order.order_number}</h1>
            <p className="text-slate-500">{format(new Date(order.created_at), 'yyyy年MM月dd日 HH:mm', { locale: ja })}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Select value={order.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-32">
              <Badge className={status.className}>{status.label}</Badge>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">受付中</SelectItem>
              <SelectItem value="confirmed">確定</SelectItem>
              <SelectItem value="shipped">発送済</SelectItem>
              <SelectItem value="completed">完了</SelectItem>
              <SelectItem value="cancelled">キャンセル</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />印刷
          </Button>
        </div>
      </div>

      {/* 印刷用ヘッダー */}
      <div className="hidden print:block text-center mb-6">
        <h2 className="text-2xl font-bold">注文明細書</h2>
        <p className="mt-2">{order.order_number}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">取引先情報</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">取引先名</p>
                  <p className="font-medium">{order.customer_name || '-'}</p>
                </div>
                {order.delivery_date && (
                  <div>
                    <p className="text-sm text-slate-500">配送希望日</p>
                    <p className="font-medium">{order.delivery_date}</p>
                  </div>
                )}
              </div>
              {order.notes && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-slate-500 mb-1">申し送り事項</p>
                  <p className="text-amber-800">{order.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">注文明細</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left p-3">商品名</th>
                    <th className="text-left p-3">産地</th>
                    <th className="text-left p-3">規格</th>
                    <th className="text-right p-3">単価</th>
                    <th className="text-right p-3">数量</th>
                    <th className="text-right p-3">小計</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items?.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {item.item_type === 'sale' && <Tag className="h-4 w-4 text-red-500" />}
                          <span className="font-medium">{item.item_name}</span>
                        </div>
                      </td>
                      <td className="p-3">{item.origin || '-'}</td>
                      <td className="p-3">{item.spec || '-'}</td>
                      <td className="p-3 text-right font-mono">¥{item.unit_price?.toLocaleString()}</td>
                      <td className="p-3 text-right">{item.quantity}</td>
                      <td className="p-3 text-right font-mono font-medium">¥{item.subtotal?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50">
                    <td colSpan={5} className="p-3 text-right font-semibold">合計</td>
                    <td className="p-3 text-right font-mono font-bold text-lg">¥{order.total_amount?.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>
        </div>

        <div className="no-print">
          <Card className="border-slate-200 sticky top-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">注文サマリー</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between">
                <span className="text-slate-500">注文番号</span>
                <span className="font-mono">{order.order_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">ステータス</span>
                <Badge className={status.className}>{status.label}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">商品点数</span>
                <span>{order.items?.length || 0}点</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">数量合計</span>
                <span>{order.items?.reduce((sum, i) => sum + i.quantity, 0) || 0}個</span>
              </div>
              <div className="border-t border-slate-200 pt-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>合計金額</span>
                  <span className="font-mono">¥{order.total_amount?.toLocaleString()}</span>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-4 text-sm text-slate-500">
                <div className="flex justify-between">
                  <span>作成日時</span>
                  <span>{format(new Date(order.created_at), 'yyyy/MM/dd HH:mm')}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span>更新日時</span>
                  <span>{format(new Date(order.updated_at), 'yyyy/MM/dd HH:mm')}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4; margin: 15mm; }
        }
      `}</style>
    </div>
  );
};
